import { off, ref, update } from "firebase/database";
import { listenDatabaseValue as onValue } from "../utils/listenDatabaseValue";
import { db } from "../lib/firebase";
import types from "../types";
import { LaunchError } from "../utils/errors";
import {
  getGlobalNotificationsUserPath,
  GLOBAL_NOTIFICATIONS_RTDB_PATH,
  mapGlobalNotificationsToAlerts,
} from "../utils/globalNotifications";

const getAuthUid = (getState) => getState()?.auth?.user?.uid || null;

export const syncGlobalNotificationsAction = (snapshot) => ({
  type: types.ALERTS_SYNC_GLOBAL_NOTIFICATIONS,
  payload: snapshot,
});

const pendingReadGlobalNotifications = (notificationIds) => ({
  type: types.ALERTS_PENDING_READ_GLOBAL_NOTIFICATIONS,
  payload: notificationIds,
});

const clearPendingReadGlobalNotifications = (notificationIds) => ({
  type: types.ALERTS_CLEAR_PENDING_READ_GLOBAL_NOTIFICATIONS,
  payload: notificationIds,
});

const pendingDismissGlobalNotification = (notificationId) => ({
  type: types.ALERTS_PENDING_DISMISS_GLOBAL_NOTIFICATION,
  payload: notificationId,
});

const clearPendingDismissGlobalNotification = (notificationId) => ({
  type: types.ALERTS_CLEAR_PENDING_DISMISS_GLOBAL_NOTIFICATION,
  payload: notificationId,
});

export const startListenerGlobalNotifications =
  ({
    uid,
    onUpdatedDataCallback = () => {},
    onReferencesAvailable = () => {},
  }) =>
  async (dispatch) => {
    try {
      if (!uid) {
        onUpdatedDataCallback({ alerts: [], readIds: [], dismissedIds: [] });
        return false;
      }

      const globalRef = ref(db, GLOBAL_NOTIFICATIONS_RTDB_PATH);
      const userRef = ref(db, getGlobalNotificationsUserPath(uid));

      onReferencesAvailable({ globalRef, userRef });

      let notificationsValue = {};
      let userValue = {};

      const emit = () => {
        onUpdatedDataCallback(
          mapGlobalNotificationsToAlerts({
            notificationsValue,
            userValue,
          })
        );
      };

      onValue(globalRef, (snapshot) => {
        notificationsValue = snapshot.val() || {};
        emit();
      });

      onValue(userRef, (snapshot) => {
        userValue = snapshot.val() || {};
        emit();
      });

      return true;
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "startListenerGlobalNotifications",
          tags: { action: "globalNotifications" },
          level: "error",
        })
      );
      return false;
    }
  };

export const stopListenerGlobalNotifications =
  ({ globalRef, userRef } = {}) =>
  () => {
    if (globalRef) {
      off(globalRef);
    }
    if (userRef) {
      off(userRef);
    }
  };

export const dismissGlobalNotification =
  (notificationId) => async (dispatch, getState) => {
    const uid = getAuthUid(getState);

    if (!uid || !notificationId) {
      return { ok: false };
    }

    dispatch(pendingDismissGlobalNotification(notificationId));

    try {
      await update(
        ref(db, `${getGlobalNotificationsUserPath(uid)}/${notificationId}`),
        { dismissed: true }
      );
      return { ok: true };
    } catch (error) {
      dispatch(clearPendingDismissGlobalNotification(notificationId));
      dispatch(
        LaunchError(error, true, [], {
          functionName: "dismissGlobalNotification",
          context: { uid, notificationId },
          tags: { action: "dismissGlobalNotification" },
          level: "error",
        })
      );
      return { ok: false };
    }
  };

export const markGlobalNotificationsAsRead =
  (notificationIds) => async (dispatch, getState) => {
    const uid = getAuthUid(getState);
    const ids = Array.isArray(notificationIds)
      ? notificationIds.filter(Boolean)
      : [];
    const pendingReadIds = getState().alerts?.pendingGlobalReadIds || [];
    const unreadIds = ids.filter(
      (id) =>
        !pendingReadIds.includes(id) &&
        getState().alerts?.globalAlerts?.some(
          (notification) => notification.id === id && !notification.isRead
        )
    );

    if (!uid || ids.length === 0) {
      return { ok: false };
    }

    if (unreadIds.length === 0) {
      return { ok: true };
    }

    dispatch(pendingReadGlobalNotifications(unreadIds));

    const updates = unreadIds.reduce((accumulator, id) => {
      accumulator[`${id}/read`] = true;
      return accumulator;
    }, {});

    try {
      await update(ref(db, getGlobalNotificationsUserPath(uid)), updates);
      return { ok: true };
    } catch (error) {
      dispatch(clearPendingReadGlobalNotifications(unreadIds));
      dispatch(
        LaunchError(error, true, [], {
          functionName: "markGlobalNotificationsAsRead",
          context: { uid, notificationIds: unreadIds },
          tags: { action: "markGlobalNotificationsAsRead" },
          level: "error",
        })
      );
      return { ok: false };
    }
  };
